"use client";

import { ReactNode, useEffect, useRef, useState } from "react";

import { FocusScope, useMenu, useMenuItem } from "react-aria";
import { Item, Node, Selection, TreeState, useTreeState } from "react-stately";

import Button from "./Button";
import { useTheme } from "./ThemeProvider";
import { useLockBodyScroll } from "../hooks/useLockBodyScroll";
import { csx, handleInternalNavigation, isUrlExternal } from "../utils";
import ScreenOverlay from "./ScreenOverlay";
import { useIsClosing } from "../hooks/useIsClosing";

import "./ActionMenu.css";

export interface ActionMenuAction {
  key: string;
  label?: ReactNode;
  to?: string;
  onAction?: (key: string) => void;
}

export interface ActionMenuProps {
  /* Whether the menu is open */
  isOpen: boolean;

  /* The selection mode of the menu */
  selectionMode?: "single" | "multiple" | "none";

  /* The keys of the selected items */
  selectedKeys?: string[];

  /* The keys of the disabled items */
  disabledKeys?: string[];

  /* The children of the menu */
  children?: ReactNode | ((props: { close: () => void }) => ReactNode);

  /* Whether to show the cancel button */
  showCancel?: boolean;

  /* The text of the cancel button */
  cancelButtonText?: string;

  /* The actions of the menu */
  actions?: ActionMenuAction[];

  /* The id of the menu */
  id?: string;

  /* The callback function to close the menu */
  onClose?: () => void;

  /* The callback function to change the selection */
  onSelectionChange?: (keys: Selection) => void;

  /* The test id of the menu */
  "data-testid"?: string;
}

/**
 * ActionMenu to display a menu of actions.
 * Renders a list of actions as a focusable menu, or non-focusable children.
 * @interface ActionMenuProps
 */
export const ActionMenu = ({
  isOpen,
  selectionMode = "single",
  selectedKeys,
  disabledKeys,
  children,
  showCancel = true,
  cancelButtonText = "Cancel",
  actions,
  onSelectionChange,
  onClose,
  "data-testid": testId,
}: ActionMenuProps) => {
  useLockBodyScroll(isOpen);
  const { className, theme } = useTheme();
  const [contentHeight, setContentHeight] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const { isClosing, handleClose } = useIsClosing({
    onClose,
    overlayRef,
  });

  const hasActions = actions && actions.length > 0;

  const handleOverlayClose = () => {
    setContentHeight(0);
    handleClose();
  };

  const state = hasActions
    ? useTreeState({
        items: actions,
        children: actions.map((action) => (
          <Item {...action} key={action.key} textValue={action.key}>
            {action.label}
          </Item>
        )),
        selectedKeys: new Set(selectedKeys),
        onSelectionChange: (keys) => onSelectionChange?.(keys),
        selectionMode,
        disabledKeys,
      })
    : null;

  const { menuProps } = hasActions
    ? useMenu(
        {
          autoFocus: true,
          shouldFocusWrap: true,
          onAction: (key) =>
            actions
              .find((action) => action.key === key)
              ?.onAction?.(key as string),
          onClose: () => onClose?.(),
          disabledKeys,
          onSelectionChange: (keys) => onSelectionChange?.(keys),
          selectionMode,
          selectedKeys,
          "aria-label": "Action Menu",
        },
        state,
        menuRef
      )
    : { menuProps: {} };

  // handle opening
  useEffect(() => {
    if (isOpen && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isOpen, contentRef]);

  const hasContent = children || hasActions || showCancel;

  if (!isOpen) return null;

  return (
    <ScreenOverlay fadeIn ref={overlayRef} onClose={handleOverlayClose}>
      <div
        className="proton-ActionMenu__background-wrapper"
        style={{
          opacity: isClosing ? 0 : 1,
        }}
      >
        <div
          data-testid={testId || "ActionMenu-wrapper"}
          className="proton-ActionMenu__wrapper"
        >
          <div
            className={csx("proton-ActionMenu__card", className)}
            data-testid="ActionMenu-content"
            style={{ height: `${contentHeight}px` }}
          >
            <div ref={menuRef}>
              <div {...menuProps} ref={contentRef}>
                {hasContent ? (
                  <FocusScope autoFocus contain>
                    {children && (
                      <div className="proton-ActionMenu__content">
                        {typeof children === "function"
                          ? children({ close: handleOverlayClose })
                          : children}
                      </div>
                    )}

                    {hasActions && (
                      <div
                        className={csx(
                          "proton-ActionMenu__list",
                          selectionMode === "none" &&
                            "proton-ActionMenu__list--disabled"
                        )}
                      >
                        {Array.from(state.collection).map((item) => (
                          <ActionMenuItem
                            key={item.key}
                            item={item}
                            state={state}
                            to={item.props.to}
                          />
                        ))}
                      </div>
                    )}

                    {showCancel && (
                      <div
                        className="proton-ActionMenu__cancel-button"
                        tabIndex={-1}
                      >
                        <Button
                          variant={
                            theme === "dark" || theme === "custom-dark"
                              ? "translucent"
                              : "secondary"
                          }
                          onPress={handleOverlayClose}
                          data-testid="ActionMenuItem-cancel"
                        >
                          {cancelButtonText}
                        </Button>
                      </div>
                    )}
                  </FocusScope>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ScreenOverlay>
  );
};

interface ActionMenuItemProps {
  to?: string;
  state: TreeState<ActionMenuAction>;
  item: Node<ActionMenuAction>;
}

const ActionMenuItem = ({ to, state, item }: ActionMenuItemProps) => {
  const ref = useRef(null);
  const { menuItemProps } = useMenuItem({ key: item.key }, state, ref);
  const Component = to ? "a" : "div";
  const isExternal = to && isUrlExternal(to);
  const anchorProps = to
    ? {
        href: to,
        target: isExternal ? "_blank" : undefined,
        rel: isExternal ? "noopener noreferrer" : undefined,
        onClick:
          isExternal || !to ? () => {} : (e) => handleInternalNavigation(e, to),
      }
    : {};

  return (
    <Component
      {...menuItemProps}
      {...anchorProps}
      key={item.key}
      className="proton-ActionMenu__item"
      aria-label={`ActionMenu-Item-${item.key}`}
      ref={ref}
    >
      {item.rendered}
    </Component>
  );
};

export default ActionMenu;
